import React, { useState } from 'react';
import { List, useTable, EditButton, ShowButton, DeleteButton } from '@refinedev/antd';
import { Table, Space, Tag, Input, Select } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { ExcelButtons } from '../../components/ExcelButtons';
import { SubscriptionPlanDto } from '@wellverse/shared-ts';

const tagStyle = (color: string) => ({
  borderColor: color,
  color,
  background: `${color}15`,
  borderRadius: 9999,
  padding: '2px 12px',
  fontWeight: 500,
});

const planTypeColors: Record<string, string> = {
  FREE: '#898989',
  BASIC: '#3b82f6',
  PRO: '#8b5cf6',
  PREMIUM: '#f59e0b',
  ENTERPRISE: '#3ecf8e',
};

export const SubscriptionPlanList: React.FC = () => {
  const [searchText, setSearchText] = useState('');
  const [planTypeFilter, setPlanTypeFilter] = useState<string | undefined>(undefined);

  const { tableProps } = useTable<SubscriptionPlanDto>({
    resource: 'subscription-plans',
    filters: {
      permanent: [
        ...(searchText ? [{ field: 'search', operator: 'contains' as const, value: searchText }] : []),
        ...(planTypeFilter ? [{ field: 'planType', operator: 'eq' as const, value: planTypeFilter }] : []),
      ],
    },
    pagination: { current: 1, pageSize: 20 },
    queryOptions: { keepPreviousData: true },
  });

  return (
    <List resource="subscription-plans">
      <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <Input.Search
          prefix={<SearchOutlined />}
          placeholder="Search by name..."
          defaultValue={searchText}
          onSearch={(value) => setSearchText(value)}
          allowClear
          style={{ width: 320 }}
        />
        <Select
          placeholder="Plan Type"
          allowClear
          style={{ width: 180 }}
          onChange={(value) => setPlanTypeFilter(value)}
          options={[
            { label: 'Free', value: 'FREE' },
            { label: 'Basic', value: 'BASIC' },
            { label: 'Pro', value: 'PRO' },
            { label: 'Premium', value: 'PREMIUM' },
            { label: 'Enterprise', value: 'ENTERPRISE' },
          ]}
        />
        <ExcelButtons resource="subscription-admin/plans" filename="subscription-plans" />
      </div>
      <Table {...tableProps} rowKey="id" scroll={{ x: 1400 }}>
        <Table.Column dataIndex="name" title="Name" />
        <Table.Column
          dataIndex="translations"
          title="Translations"
          render={(translations: Record<string, Record<string, string>> | null) => {
            if (!translations) return '-';
            const locales = Object.keys(translations);
            if (locales.length === 0) return '-';
            return locales.map((l) => translations[l]?.name).filter(Boolean).join(' / ');
          }}
        />
        <Table.Column
          dataIndex="planType"
          title="Type"
          render={(type: string) => (
            <Tag style={tagStyle(planTypeColors[type] || '#898989')}>{type}</Tag>
          )}
        />
        <Table.Column
          dataIndex="priceMonthlyCents"
          title="Monthly (cents)"
          render={(v: number) => `$${(v / 100).toFixed(2)}`}
        />
        <Table.Column
          dataIndex="priceYearlyCents"
          title="Yearly (cents)"
          render={(v: number | null) => v != null ? `$${(v / 100).toFixed(2)}` : '-'}
        />
        <Table.Column dataIndex="trialDays" title="Trial Days" render={(v: number | null) => v ?? '-'} />
        <Table.Column
          dataIndex="isActive"
          title="Active"
          render={(isActive: boolean) => (
            <Tag style={tagStyle(isActive ? '#3ecf8e' : '#898989')}>
              {isActive ? 'Active' : 'Inactive'}
            </Tag>
          )}
        />
        <Table.Column dataIndex="sortOrder" title="Sort" />
        <Table.Column
          title="Actions"
          dataIndex="actions"
          render={(_: any, record: SubscriptionPlanDto) => (
            <Space>
              <EditButton hideText size="small" recordItemId={record.id} resource="subscription-plans" />
              <ShowButton hideText size="small" recordItemId={record.id} resource="subscription-plans" />
              <DeleteButton hideText size="small" recordItemId={record.id} resource="subscription-plans" />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
